import { useState, useEffect } from 'react';
import Api from '../Network';
import { message } from 'antd';

export interface DashboardStats {
  totalProducts: number;
  totalCustomers: number;
  totalOrders: number;
  totalSales: string;
  pendingOrders: number;
}

export interface RecentOrder {
  id: number;
  orderId: string;
  customer: string;
  amount: string;
  status: string;
  date: string;
}

export interface MonthlyStats {
  invoices: {
    current: number;
    previous: number;
    change: number;
  };
  receipts: {
    current: number;
    previous: number;
    change: number;
  };
}

export interface MonthlySalesPoint {
  month: string;
  total_sales?: number;
  paid_amount?: number;
  invoice_count: number;
  invoice_status?: string;
  status?: string;
  payment_method?: string;
  type?: string;
  owner?: any;
  inventory?: any;
  invoicePayments?: any[];
}

export interface MonthlyReceiptPoint {
  month: string;
  amount?: number;
  receipt_count?: number;
}

export interface MonthlyVisitPoint {
  month: string;
  visit_count?: number;
}

export interface StatusCount {
  status: string;
  count: number;
}

export interface ChartData {
  monthlySales: MonthlySalesPoint[];
  monthlyReceipts: MonthlyReceiptPoint[];
  ordersByStatus: StatusCount[];
  visitsByStatus: StatusCount[];
  monthlyVisits: MonthlyVisitPoint[];
  totalVisits: number;
}

export interface DashboardStatistics {
  stats: DashboardStats;
  recentOrders: RecentOrder[];
  monthlyStats: MonthlyStats;
  chartData: ChartData;
  loading: boolean;
  error: string | null;
}

export const useDashboardStatistics = (): DashboardStatistics => {
  const [data, setData] = useState<DashboardStatistics>({
    stats: {
      totalProducts: 0,
      totalCustomers: 0,
      totalOrders: 0,
      totalSales: '0.00',
      pendingOrders: 0,
    },
    recentOrders: [],
    monthlyStats: {
      invoices: { current: 0, previous: 0, change: 0 },
      receipts: { current: 0, previous: 0, change: 0 },
    },
    chartData: {
      monthlySales: [],
      monthlyReceipts: [],
      ordersByStatus: [],
      visitsByStatus: [],
      monthlyVisits: [],
      totalVisits: 0,
    },
    loading: true,
    error: null,
  });

  const fetchDashboardStatistics = async () => {
    try {
      setData((prev) => ({ ...prev, loading: true, error: null }));

      const response = await Api.get('/api/admin/dashboard/statistics');

      if (
        response &&
        typeof response.status === 'boolean' &&
        response.status === true &&
        response.data
      ) {
        const incomingChart = response.data.chartData ?? {};
        setData({
          stats: response.data.stats,
          recentOrders: response.data.recentOrders,
          monthlyStats: response.data.monthlyStats,
          chartData: {
            monthlySales: incomingChart.monthlySales ?? [],
            monthlyReceipts: incomingChart.monthlyReceipts ?? [],
            ordersByStatus: incomingChart.ordersByStatus ?? [],
            visitsByStatus: incomingChart.visitsByStatus ?? [],
            monthlyVisits: incomingChart.monthlyVisits ?? [],
            totalVisits: incomingChart.totalVisits ?? 0,
          },
          loading: false,
          error: null,
        });
      } else {
        throw new Error('Invalid response format from dashboard API');
      }
    } catch (error: any) {
      setData((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to load dashboard statistics',
      }));

      // Only show error message if it's not a successful response
      if (error.response?.status !== 200) {
        message.error('Failed to load dashboard statistics');
      }
    }
  };

  useEffect(() => {
    fetchDashboardStatistics();
  }, []);

  return data;
};
